/* eslint-disable import/no-named-as-default-member */
import React, { useEffect, useState, useCallback } from 'react';
import rest from 'util/Api';
import JsDownload from 'js-file-download';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { selectGlobals } from 'redux/globals/selectors';
import usePackageWithoutZones from 'redux/packagesWithoutZones';
import { actions } from 'redux/packagesWithoutZones/slice';
import { actions as paquerActions } from 'redux/paquers/slice';
import {
  packagesWithoutZonePaginationSelector,
  packagesWithoutZoneItemsSelector,
  packagesWithoutZonesDateSelector,
  packagesWithoutZonesLoadedSelector,
  packagesWithoutZonesSearchSelector,
  packagesWithoutZonesStatusSelector,
  packagesWithoutZonesTypeSelector,
} from 'redux/packagesWithoutZones/selectors';
import { isReadyForDeliveryStatus } from 'constants/packages';
import API from 'constants/api';
import SITELINKS from 'constants/sitelinks';
import { TableDateTimeFormat, ExportDateFormat } from 'constants/dateFormats';
import { notification } from 'antd';
import PaqueryTable from '@paquery-team/lib-table';
import ViewPackageButton from 'components/viewPackageButton';
import DownloadLabelButton from 'components/downloadLabelButton';
import externalCodeColumn from 'components/tables/columns';
import PaquerSettingsButton from 'components/paquerSettingsButton';
import usePaquers from 'redux/paquers';
import SizeIndicator from 'components/sizeIndicator';
import EditRecordButton from 'components/editRecordButton';
import { useOnTransitStatusOptions } from 'hooks/useOptions';
import EditDropdownButton from 'components/editRecordButton/EditDropdownButton';

const downloadLabelColumn = {
  render: (_, record) => (
    <DownloadLabelButton
      packageId={record.key}
      externalCode={record.externalCode}
    />
  ),
};

const destinationPersonNameColumn = {
  title: 'Nombre destinatario',
  dataIndex: 'destinationPersonName',
};

const destinationAddressColumn = {
  title: 'Destino',
  dataIndex: 'destinationAddress',
};

const deliveryTermColumn = {
  title: 'Plazo',
  dataIndex: 'deliveryTermDescription',
};

const arrivedAtPaqueryPointDateColumn = {
  title: 'Arribó a PaqueryPoint',
  dataIndex: 'arrivedAtPaqueryPointDate',
  align: 'center',
};

const statusColumn = {
  title: 'Estado',
  dataIndex: 'statusDescription',
};

const sizeColumn = {
  title: 'Tamaño',
  dataIndex: 'packageSizeDescription',
  align: 'center',
  render: (text) => <SizeIndicator text={text[0]} />,
};

const editColumn = {
  align: 'center',
  render: (_, record) => (
    <EditRecordButton record={record} link={`${SITELINKS.packages.list}/id`} />
  ),
};

const viewPackageColumn = {
  align: 'center',
  render: (_, record) => <ViewPackageButton packageId={record.id} />,
};

const paquerSettingsColumn = {
  align: 'center',
  render: (_, record) => (
    <PaquerSettingsButton actions={actions} packageId={record} isWithoutZone />
  ),
};

const dataColumns = [
  externalCodeColumn,
  destinationPersonNameColumn,
  destinationAddressColumn,
  deliveryTermColumn,
  sizeColumn,
  arrivedAtPaqueryPointDateColumn,
  statusColumn,
];

const PackagesWithoutZone = () => {
  usePackageWithoutZones();
  usePaquers();
  const dispatch = useDispatch();
  const globals = useSelector(selectGlobals);
  const [disabled, setDisabled] = useState(false);
  const items = useSelector(packagesWithoutZoneItemsSelector);
  const pageable = useSelector(packagesWithoutZonePaginationSelector);
  const loaded = useSelector(packagesWithoutZonesLoadedSelector);
  const status = useSelector(packagesWithoutZonesStatusSelector);
  const packageType = useSelector(packagesWithoutZonesTypeSelector);
  const search = useSelector(packagesWithoutZonesSearchSelector);
  const date = useSelector(packagesWithoutZonesDateSelector);
  const [selectedPackagesId, setSelectedPackagesId] = useState([]);
  const filteredStatus = [...useOnTransitStatusOptions()];

  const editColumn = {
    align: 'center',
    render: (_, record) => (
      <EditDropdownButton
        record={record}
        link={`${SITELINKS.packages.list}/id`}
        onEditAddressSuccess={() => {
          setTimeout(() => {
            dispatch(actions.refreshPage());
          }, 1500);
        }}
      />
    ),
  };

  const actionColumns = [paquerSettingsColumn, viewPackageColumn, editColumn];

  const columnsLargeDevice = [
    downloadLabelColumn,
    ...dataColumns,
    ...actionColumns,
  ];

  const columnsMediumDevice = [
    downloadLabelColumn,
    externalCodeColumn,
    statusColumn,
    ...actionColumns,
  ];

  const columnsSmallDevice = [
    downloadLabelColumn,
    externalCodeColumn,
    ...actionColumns,
  ];

  useEffect(() => {
    dispatch(paquerActions.initial());
  }, [dispatch]);

  const handleExportCsv = async () => {
    setDisabled(true);
    try {
      const searchParams = new URLSearchParams({
        search,
        status: status ?? '',
        packageType,
        from: date.from,
        to: date.to,
      });
      const response = await rest.get(
        `${API.reports.withoutZone}?${searchParams.toString()}`,
        {
          responseType: 'blob',
        },
      );
      const filename = `Paquetes-Sin-Zona-${dayjs()
        .tz()
        .format(ExportDateFormat)}.xls`;
      JsDownload(response.data, filename);
    } catch (exception) {
      // eslint-disable-next-line no-console
      console.error(exception);
      notification.error({
        message: 'Ha ocurrido un error al traer los paquetes',
        description:
          'Intente nuevamente por favor o informelo a los administradores',
      });
    } finally {
      setDisabled(false);
    }
  };

  const rowSelection = {
    type: 'checkbox',
    columnWidth: '30px',
    hideDefaultSelections: true,
    selectedRowKeys: selectedPackagesId,
    onChange: (selectedRowKeys) => {
      setSelectedPackagesId(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: !record.isDeliverable,
      name: record.externalCode,
    }),
  };

  const updatePaginate = useCallback(
    (page) => {
      dispatch(actions.updatePagination(page));
    },
    [dispatch],
  );
  const dateSelectionCallback = useCallback(
    (newDate) => {
      dispatch(actions.updateDate(newDate));
    },
    [dispatch],
  );
  const searchCallback = useCallback(
    (searchText) => {
      dispatch(actions.updateSearch(searchText));
    },
    [dispatch],
  );
  const searcher = {
    onSearching: searchCallback,
    placeholder: 'Código o destinatario',
    allowEmptySearch: true,
  };
  const selectCallback = useCallback(
    (newStatus) => {
      dispatch(actions.updateStatus(newStatus));
    },
    [dispatch],
  );
  const selectPackageTypeCallback = useCallback(
    (newPackageType) => {
      dispatch(actions.updatePackageType(newPackageType));
    },
    [dispatch],
  );
  const selectors = [
    {
      onChange: selectPackageTypeCallback,
      placeholder: 'Tipo de envio',
      list: globals.packages.type,
    },
    {
      onChange: selectCallback,
      placeholder: 'Estado',
      list: filteredStatus,
    },
  ];
  let filteredPackages = [];
  if (loaded && items) {
    filteredPackages = items.map((packet) => ({
      ...packet,
      key: packet.id,
      externalCode: packet.externalCode,
      destinationPersonName: packet.shippingScheduleDestination.name,
      destinationAddress:
        packet.shippingScheduleDestination.shippingAddress.addressDetail,
      deliveryTermDescription: packet.deliveryTerm
        ? globals.deliveryTerm.find(
            (term) => term.value === packet.deliveryTerm,
          ).description
        : '',
      packageSizeDescription: globals.packages.size.find(
        (packSize) => packSize.value === packet.packageSize,
      ).name,
      arrivedAtPaqueryPointDate: packet.arrivedAtPaqueryPointDate
        ? dayjs(packet.arrivedAtPaqueryPointDate)
            .tz()
            .format(TableDateTimeFormat)
        : null,
      statusDescription: globals.packages.status.find(
        (state) => state.value === packet.status,
      ).name,
      isDeliverable: isReadyForDeliveryStatus(packet.status),
    }));
  }
  return (
    <PaqueryTable
      rowSelection={rowSelection}
      loading={!loaded}
      header={{
        selectors,
        searcher,
        rangePicker: {
          onDateSelection: dateSelectionCallback,
          required: {
            value: true,
            message: 'La fecha es requerida',
          },
        },
        onExportCsv: { callback: handleExportCsv, disabled },
        refresh: () => dispatch(actions.refreshPage()),
        title: 'Sin zona',
      }}
      usePackageRowColors
      onChangePaginate={updatePaginate}
      dataSource={filteredPackages}
      paginate={pageable}
      dataColumns={dataColumns}
      colsForSmallDevice={columnsSmallDevice}
      colsForMediumDevice={columnsMediumDevice}
      colsForLargeDevice={columnsLargeDevice}
    />
  );
};

export default PackagesWithoutZone;
